import * as THREE from "three";
import Scenes from "./Scenes";
import {v2,v3} from "./short3";
import Maths3 from "./Maths3";

/**
 * Utility-Klasse in konkurrenz zu Screens. ScreenUtils ist nicht statisch,
 * sondern bezieht sich auf eine camera und ein canvasRect zu einem bestimmten Zeitpunkt.
 */
export class ScreenUtils {

    /**
     * @type {}THREE.PerspectiveCamera}
     */
    camera;

    /**
     * @type {DOMRect}
     */
    canvasRect;

    constructor( camera=APP.camera,
                 canvasRect=APP.renderer.domElement.getBoundingClientRect()) {
        this.camera=camera;
        this.camera.updateMatrixWorld();
        this.camera.updateProjectionMatrix();
        // this.camera.clearViewOffset()
        this.canvasRect=canvasRect;
    }

    getRenderSize() {
        const r = this.canvasRect;
        return v2(r.width,r.height);
    }

    worldToScreenCoordinates(worldPosition) {
        const camera = this.camera;
        const rect = this.canvasRect;
        const screenPosition = worldPosition.clone().project(camera);
        return v2(
            (screenPosition.x + 1) / 2 * rect.width + rect.left,
            -(screenPosition.y - 1) / 2 * rect.height + rect.top
        );
    }

    screenWorldRatios() {
        const screen = this.getRenderSize();
        // FIXME korrekt mit effective fov?
        const vfov = this.camera.getEffectiveFOV() * Math.PI / 180;
        const hfov = Math.atan(this.camera.aspect * Math.tan(vfov / 2)) * 2;
        const hFactor = Math.max(screen.x,1) / (2 * Math.tan(hfov / 2));
        const vFactor = Math.max(screen.y,1) / (2 * Math.tan(vfov / 2));
        return {
            hfov,
            vfov,
            hFactor,
            vFactor,
        };
    }

    objectToScreenBoxExpensive(object) {
        const box = new THREE.Box2();
        Scenes.traverseVertices(object,v=>{
            box.expandByPoint(this.worldToScreenCoordinates(v.clone()));
        });
        return box;
    }

    worldSphereToScreen(worldSphere) {
        const center = this.worldToScreenCoordinates(worldSphere.center);
        const {hFactor,vFactor} = this.screenWorldRatios();
        const radius = Math.max(hFactor,vFactor) * worldSphere.radius  / this.camera.position.distanceTo(worldSphere.center);
        return {center,radius};
    }

    screenToDeviceCoordinates(screenPos) {
        const rect = this.canvasRect;
        const x = screenPos.x - rect.left;
        const y = screenPos.y - rect.top;
        const deviceCoords = v2();
        deviceCoords.x = ( x / rect.width ) * 2 - 1;
        deviceCoords.y = -( y / rect.height ) * 2 + 1;
        return deviceCoords;
    }

    mouseRaycaster(screenPos) {
        const raycaster = new THREE.Raycaster();
        const deviceCoords = this.screenToDeviceCoordinates(screenPos);
        raycaster.setFromCamera(deviceCoords,this.camera);
        return raycaster;
    }

    screenToWorldCoordinates(screenPos) {
        return this.mouseRaycaster(screenPos).ray.at(screenPos.z);
    }

    screenToCameraCoordinates(screenPos) {
        const deviceCoords = Maths3.toV3(this.screenToDeviceCoordinates(screenPos),screenPos.z);
        const worldPoint = deviceCoords.unproject( this.camera );
        const cameraSpace = worldPoint.applyMatrix4( this.camera.matrixWorldInverse );
        return cameraSpace;
    }

    getDistanceToCameraWhereWorldUnitsEqualsPixel(boundingClientRect, sizeInWorldUnits, spherical=false) {
        const r = boundingClientRect;
        const s = sizeInWorldUnits;
        const {hFactor,vFactor} = this.screenWorldRatios();
        if (spherical) {
            return Math.max(hFactor,vFactor) * Math.max(s.x,s.y) / Math.min(r.width,r.height);
        }
        return Math.max(hFactor*s.x/r.width, vFactor*s.y/r.height );
    }

    getWorldCenterToFitSizeIntoScreenRect(boundingClientRect, sizeInWorldUnits, spherical) {
        const z = this.getDistanceToCameraWhereWorldUnitsEqualsPixel(boundingClientRect, sizeInWorldUnits, spherical);
        const r = boundingClientRect;
        const w = this.screenToWorldCoordinates(v3(r.x+r.width/2, r.y+r.height/2, z));
        return w;
    }

    setCameraScreenCenter(center) {
        const canvasBox = this.rectToBox(this.canvasRect);
        const delta = canvasBox.getCenter().sub(center);
        const size = canvasBox.getSize();
        this.camera.setViewOffset(size.x,size.y,delta.x,delta.y,size.x,size.y);
    }

    rectToBox(r) {
        return r==null ? null : new THREE.Box2(v2(r.x,r.y),v2(r.x+r.width,r.y+r.height));
    }
}
